import { useCopilotAction } from '@copilotkit/react-core'
import { useMemo } from 'react'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'

import {
  PreviewAllowance,
  PreviewArgs,
  TimeoffPolicyChangePreview,
} from 'modules/ai/components/TimeoffPolicyChangePreview'

function isRecordOfUnknown(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

function isStringField(value: unknown): value is string {
  return typeof value === 'string'
}

function parseTimeoffPreviewUnit(value: unknown): 'days' | 'hours' | null {
  return value === 'days' || value === 'hours' ? value : null
}

function parseTimeoffPreviewAllowanceOptions(
  value: unknown
): PreviewAllowance['targetAllowanceOptions'] {
  if (!Array.isArray(value)) return undefined

  const parsedOptions: NonNullable<PreviewAllowance['targetAllowanceOptions']> = []
  for (const raw of value) {
    if (!isRecordOfUnknown(raw)) return undefined
    const { id, name, unit } = raw
    const parsedUnit = parseTimeoffPreviewUnit(unit)

    if (!isStringField(id) || !isStringField(name) || !parsedUnit) return undefined

    parsedOptions.push({ id, name, unit: parsedUnit })
  }

  return parsedOptions
}

function parseTimeoffPreviewAllowance(value: unknown): PreviewAllowance | null {
  if (!isRecordOfUnknown(value)) return null
  const {
    sourceAllowanceId,
    sourceAllowanceName,
    sourceUnit,
    sourceAvailableBalance,
    targetAllowanceId,
    targetAllowanceName,
    targetUnit,
    targetAllowanceOptions,
  } = value

  if (
    !isStringField(sourceAllowanceId) ||
    !isStringField(sourceAllowanceName) ||
    !isStringField(sourceAvailableBalance)
  ) {
    return null
  }

  const parsedSourceUnit = parseTimeoffPreviewUnit(sourceUnit)
  if (!parsedSourceUnit) return null

  return {
    sourceAllowanceId,
    sourceAllowanceName,
    sourceUnit: parsedSourceUnit,
    sourceAvailableBalance,
    targetAllowanceId: isStringField(targetAllowanceId) ? targetAllowanceId : null,
    targetAllowanceName: isStringField(targetAllowanceName) ? targetAllowanceName : null,
    targetUnit: parseTimeoffPreviewUnit(targetUnit),
    targetAllowanceOptions: parseTimeoffPreviewAllowanceOptions(targetAllowanceOptions),
  }
}

function parseTimeoffPreviewArgs(value: unknown): PreviewArgs | null {
  if (!isRecordOfUnknown(value)) return null
  const {
    employeeId,
    employeeName,
    sourcePolicyId,
    sourcePolicyName,
    targetPolicyId,
    targetPolicyName,
    effectiveAt,
    allowances,
  } = value

  if (
    !isStringField(employeeId) ||
    !isStringField(employeeName) ||
    !isStringField(sourcePolicyId) ||
    !isStringField(sourcePolicyName) ||
    !isStringField(targetPolicyId) ||
    !isStringField(targetPolicyName) ||
    !isStringField(effectiveAt) ||
    !Array.isArray(allowances)
  ) {
    return null
  }

  const parsedAllowances: PreviewAllowance[] = []
  for (const raw of allowances) {
    const parsed = parseTimeoffPreviewAllowance(raw)
    if (!parsed) return null
    parsedAllowances.push(parsed)
  }

  return {
    employeeId,
    employeeName,
    sourcePolicyId,
    sourcePolicyName,
    targetPolicyId,
    targetPolicyName,
    effectiveAt,
    allowances: parsedAllowances,
  }
}

function PreviewTimeoffPolicyChangeAction({ args }: { args: unknown }) {
  const previewArgs = useMemo(() => parseTimeoffPreviewArgs(args), [args])
  if (!previewArgs) return <></>

  return (
    <ErrorBoundary blankSlate>
      <TimeoffPolicyChangePreview args={previewArgs} />
    </ErrorBoundary>
  )
}

export const usePreviewTimeoffPolicyChangeAction = () => {
  useCopilotAction({
    name: 'previewTimeoffPolicyChange',
    available: 'remote',
    followUp: false,
    description:
      'Render an interactive timeoff policy-change widget that lets the user pick transfer/discard per allowance and applies the change directly. The widget runs autonomously — no follow-up tool call from the agent is needed or expected.',
    parameters: [
      { name: 'employeeId', required: true },
      { name: 'employeeName', required: true },
      { name: 'sourcePolicyId', required: true },
      { name: 'sourcePolicyName', required: true },
      { name: 'targetPolicyId', required: true },
      { name: 'targetPolicyName', required: true },
      { name: 'effectiveAt', required: true },
      { name: 'allowances', type: 'object[]', required: true },
    ],
    // Fire-and-forget: the widget owns the whole interaction, including the mutation.
    render: ({ args }) => <PreviewTimeoffPolicyChangeAction args={args} />,
  })
}
